/**
 * Teams — no prototype counterpart. A sales manager self-serving their own
 * team's creation (and roster) is a requirement with no design mock behind
 * it; see `salescrmapi/src/modules/teams/teams.service.js`'s header note and
 * this module's build conversation for the decisions that shaped it.
 *
 * "My teams" defaults on for anyone without `teams:delete` (i.e. everyone but
 * superadmin) — `teams:view` sees every team in the org with no scope
 * narrowing (same as `campaigns:view` — see that module's identical note), so
 * without a default filter a manager's own "My Team" screen would open onto
 * every team in the company.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { Badge, SearchInput, MockBadge, ErrorState } from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { isMock } from '../../api/mockFallback.js';
import { number } from '../../utils/formatters.js';
import { useTeams, useUserPickerOptions, useCreateTeam, useAddTeamMembers } from './useTeams.js';
import { toRow } from './teamShape.js';
import { CreateTeamModal } from './TeamModals.jsx';

export default function TeamsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canCreate = can('teams', 'create');
  const canDelete = can('teams', 'delete');

  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(!canDelete);
  const [createOpen, setCreateOpen] = useState(false);

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);
  const userOptions = useUserPickerOptions();

  const params = useMemo(() => ({
    ...pages.params,
    q: debounced || undefined,
    manager_id: onlyMine ? 'me' : undefined,
  }), [pages.params, debounced, onlyMine]);

  const { data, isLoading, error, refetch } = useTeams(params);
  const rows = useMemo(() => (data?.data ?? []).map(toRow), [data]);

  const createMutation = useCreateTeam();
  const addMembersMutation = useAddTeamMembers();

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const columns = [
    {
      key: 'name',
      header: 'Team',
      render: (t) => (
        <div className="min-w-0">
          <a
            href={`/teams/${t.id}`}
            className="fw-600 d-block text-truncate"
            onClick={(e) => { e.preventDefault(); navigate(`/teams/${t.id}`); }}
          >
            {t.name}
          </a>
          <div className="t-xs text-muted text-truncate">{t.path}</div>
        </div>
      ),
    },
    {
      key: 'manager', header: 'Lead', width: 170,
      render: (t) => t.manager?.full_name ?? <span className="text-muted">—</span>,
    },
    {
      key: 'parent', header: 'Parent team', width: 170,
      render: (t) => t.parent?.name ?? <span className="text-muted">Top-level</span>,
    },
    {
      key: 'members', header: 'Members', width: 100,
      render: (t) => number(t.memberCount),
    },
    {
      key: 'status', header: 'Status', width: 110,
      render: (t) => <Badge tone={t.statusTone}>{t.statusLabel}</Badge>,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><span>Workspace</span><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" /><span>My Team</span></div>
          <h1 className="page-title">My Team</h1>
          <p className="page-sub">
            {number(data?.page?.total ?? rows.length)} teams
            {isMock(data) && <MockBadge section="teams" />}
          </p>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          {canCreate && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setCreateOpen(true)}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />Create team
            </button>
          )}
        </div>
      </div>

      <div className="card-x fade-up">
        <div className="d-flex flex-wrap align-items-center gap-2 p-3 border-bottom">
          <div style={{ maxWidth: 240 }}>
            <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search teams…" />
          </div>
          <button
            type="button" className={`chip ${onlyMine ? 'active' : ''}`}
            onClick={() => onFilter(setOnlyMine)(!onlyMine)}
          >
            My teams {onlyMine && <i className="bi bi-x-lg x" aria-hidden="true" />}
          </button>
        </div>

        {error ? (
          <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            page={data?.page}
            onNext={pages.goNext}
            onPrevious={pages.goPrevious}
            canGoPrevious={pages.canGoPrevious}
            onRowClick={(t) => navigate(`/teams/${t.id}`)}
            emptyTitle={onlyMine ? "You don't lead a team yet" : 'No teams match these filters'}
          />
        )}
      </div>

      <CreateTeamModal
        open={createOpen}
        userOptions={userOptions}
        busy={createMutation.isPending || addMembersMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload, memberIds) => {
          const team = await createMutation.mutateAsync(payload);
          if (memberIds.length) await addMembersMutation.mutateAsync({ id: team.id, userIds: memberIds });
          setCreateOpen(false);
          navigate(`/teams/${team.id}`);
        }}
      />
    </>
  );
}
